import { StyleSheet } from 'aphrodite';
import {
	brandPrimary,
	brandPrimaryActive,
	brandPrimaryHover,
	brandSecondary,
	brandSecondaryActive,
	brandSecondaryHover,
	midGrey,
	white,
} from '../../styles/colors';
import { ctaButtonBase } from '../../styles/styles';

// Define the enum directly in this file to avoid circular dependency
export enum CompoundButtonType {
	CtaPrimary = 'CtaPrimary',
	CtaReverse = 'CtaReverse',
	CtaSecondary = 'CtaSecondary',
	CtaReverseSmall = 'CtaReverseSmall',
}

// Helper function to get the appropriate fill color for the disclosure icon based on button type
export const getDisclosureIconFillColor = (buttonType: CompoundButtonType): string => {
	const isPrimary = buttonType === CompoundButtonType.CtaPrimary || buttonType === CompoundButtonType.CtaSecondary;
	return isPrimary ? white : midGrey;
};

export const styleSheet = StyleSheet.create({
	container: {
		display: 'inline-block',
		position: 'relative',
	},
	buttonGroup: {
		display: 'flex',
		border: `1px solid ${brandPrimary}`,
		borderRadius: 0,
		overflow: 'hidden',
		width: 120,
		height: 28,
	},
	buttonGroupPrimary: {
		display: 'flex',
		border: 'none',
		borderRadius: 8,
		overflow: 'hidden',
		width: 120,
		height: 28,
	},
	// Base button styles
	baseMainButton: {
		...ctaButtonBase,
		border: 'none',
		width: 90,
		padding: '5px 0',
		height: '100%',
		fontSize: 14,
		lineHeight: '18px',
	},
	baseTriggerButton: {
		...ctaButtonBase,
		position: 'relative',
		border: 'none',
		width: 30,
		maxWidth: 30,
		minWidth: 30,
		padding: 0,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	// Button type specific styles
	[`${CompoundButtonType.CtaPrimary}`]: {
		background: brandPrimary,
		color: white,
		borderRadius: '8px 0 0 8px',
		':hover': {
			background: brandPrimaryHover,
		},
		':active': {
			background: brandPrimaryActive,
		},
	},
	[`${CompoundButtonType.CtaPrimary}-triggerButton`]: {
		background: brandPrimary,
		color: white,
		borderLeft: '1px solid rgba(255, 255, 255, 0.3)',
		borderRadius: 0,
		borderTopRightRadius: 8,
		borderBottomRightRadius: 8,
		':hover': {
			background: brandPrimaryHover,
		},
		':active': {
			background: brandPrimaryActive,
		},
	},
	[`${CompoundButtonType.CtaPrimary}-buttonGroup`]: {
		display: 'flex',
		border: 'none',
		borderRadius: 8,
		overflow: 'hidden',
		width: 120,
		height: 28,
	},
	[`${CompoundButtonType.CtaPrimary}-dropdown`]: {
		position: 'absolute',
		left: 0,
		zIndex: 1,
		width: '100%',
		background: white,
		border: 'none',
		borderRadius: 8,
		marginTop: 2,
	},
	[`${CompoundButtonType.CtaSecondary}`]: {
		background: brandSecondary,
		color: white,
		borderRadius: '8px 0 0 8px',
		':active': {
			background: brandSecondaryActive,
		},
		':hover': {
			background: brandSecondaryHover,
		},
	},
	[`${CompoundButtonType.CtaSecondary}-triggerButton`]: {
		background: brandSecondary,
		color: white,
		borderLeft: '1px solid rgba(255, 255, 255, 0.3)',
		borderRadius: 0,
		borderTopRightRadius: 8,
		borderBottomRightRadius: 8,
		':hover': {
			background: brandSecondaryHover,
		},
		':active': {
			background: brandSecondaryActive,
		},
	},
	[`${CompoundButtonType.CtaSecondary}-buttonGroup`]: {
		display: 'flex',
		border: 'none',
		borderRadius: 8,
		overflow: 'hidden',
		width: 120,
		height: 28,
	},
	[`${CompoundButtonType.CtaSecondary}-dropdown`]: {
		position: 'absolute',
		left: 0,
		zIndex: 1,
		width: '100%',
		background: white,
		border: 'none',
		borderRadius: 8,
		marginTop: 2,
	},
	[`${CompoundButtonType.CtaReverse}`]: {
		background: white,
		border: 'none',
		borderRadius: 0,
		color: brandPrimary,
		':hover': {
			background: '#F3F8FB',
		},
		':active': {
			background: '#E8F3F9',
		},
	},
	[`${CompoundButtonType.CtaReverse}-triggerButton`]: {
		background: white,
		borderLeft: `1px solid ${brandPrimary}`,
		borderRadius: 0,
		':hover': {
			background: '#F3F8FB',
		},
		':active': {
			background: '#E8F3F9',
		},
	},
	[`${CompoundButtonType.CtaReverse}-buttonGroup`]: {
		display: 'flex',
		border: `1px solid ${brandPrimary}`,
		borderRadius: 0,
		overflow: 'hidden',
		width: 120,
		height: 28,
	},
	[`${CompoundButtonType.CtaReverse}-dropdown`]: {
		position: 'absolute',
		left: 0,
		zIndex: 1,
		width: '100%',
		background: white,
		border: `1px solid ${brandPrimary}`,
		borderRadius: 0,
		boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
		marginTop: 2,
	},
	[`${CompoundButtonType.CtaReverseSmall}`]: {
		background: white,
		color: midGrey,
		padding: '4px 14px',
		minWidth: 60,
		fontSize: 14,
		':active': {
			background: '#E6ECF3',
		},
		':hover': {
			background: '#EAF7FA',
			border: `1px solid ${brandPrimary}`,
		},
	},
	[`${CompoundButtonType.CtaReverseSmall}-triggerButton`]: {
		background: white,
		borderLeft: `1px solid ${brandPrimary}`,
		borderRadius: 0,
		padding: '4px 0px',
		minWidth: 24,
		fontSize: 14,
		':hover': {
			background: '#F3F8FB',
		},
		':active': {
			background: '#E8F3F9',
		},
	},
	[`${CompoundButtonType.CtaReverseSmall}-buttonGroup`]: {
		display: 'flex',
		border: `1px solid ${brandPrimary}`,
		borderRadius: 0,
		overflow: 'hidden',
		width: 120,
		height: 28,
	},
	[`${CompoundButtonType.CtaReverseSmall}-dropdown`]: {
		position: 'absolute',
		left: 0,
		zIndex: 1,
		width: '100%',
		background: white,
		border: `1px solid ${brandPrimary}`,
		borderRadius: 0,
		boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
		marginTop: 2,
	},
	// Utility styles
	chevronIcon: {
		transition: 'transform 0.2s ease',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		position: 'absolute',
		left: '50%',
		top: '50%',
		transform: 'translate(-50%, -50%) rotate(90deg)',
	},
	chevronIconRotated: {
		transform: 'translate(-50%, -50%) rotate(270deg)',
	},
	dropdownMenuDown: {
		top: '100%',
	},
	dropdownMenuUp: {
		bottom: '100%',
		marginTop: 0,
		marginBottom: 2,
	},
	menuItem: {
		width: '100%',
		textAlign: 'center',
		padding: '4px 16px',
		background: 'transparent',
		border: 'none',
		cursor: 'pointer',
		':hover': {
			background: '#F3F8FB',
		},
	},
});
